import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { withRouter } from '../withRouter';
import BlockRenderer from './BlockRenderer';
import ArticleCard from './ArticleCard';
import articlesService from '../../services/articles.service';
import { cmsImageUrl } from 'helpers/cmsWebp';
import styles from './ArticleDetail.module.scss';

class ArticleDetail extends Component {
    static displayName = ArticleDetail.name;

    constructor(props) {
        super(props);
        this.state = {
            article: null,
            loading: true,
            error: null,
            isPreview: false,
        };
    }

    componentDidMount() {
        this.loadArticle();
    }

    componentDidUpdate(prevProps) {
        const prevSlug = prevProps.params?.slug;
        const currentSlug = this.props.params?.slug;
        if (prevSlug !== currentSlug) {
            this.loadArticle();
        }
    }

    loadArticle = async () => {
        const slug = this.props.params?.slug;
        if (!slug) return;

        this.setState({ loading: true, error: null });

        try {
            // Check for preview mode (?preview=true&token=...&t=...)
            const searchParams = new URLSearchParams(window.location.search);
            const isPreview = searchParams.get('preview') === 'true';
            const token = searchParams.get('token');
            const t = searchParams.get('t');

            let article;
            if (isPreview && token && t) {
                article = await articlesService.getArticlePreview(slug, token, t);
            } else {
                article = await articlesService.getArticleBySlug(slug);
            }

            this.setState({ article, loading: false, isPreview });
            window.scrollTo(0, 0);
        } catch (e) {
            console.error('[ArticleDetail] Error loading article:', e);
            this.setState({ error: 'Article not found', loading: false });
        }
    }

    render() {
        const { article, loading, error, isPreview } = this.state;

        if (loading) {
            return (
                <div className={styles.articleDetail}>
                    <div className={styles.cloudBG} />
                    <Container>
                        <div className={styles.loadingText}>Loading article...</div>
                    </Container>
                </div>
            );
        }

        if (error || !article) {
            return (
                <div className={styles.articleDetail}>
                    <div className={styles.cloudBG} />
                    <Container className="text-center">
                        <div className={styles.loadingText}>{error || 'Article not found'}</div>
                        <Link to="/articles" className={`${styles.button} ${styles.small} ${styles.alternate}`}>Back to Articles</Link>
                    </Container>
                </div>
            );
        }

        const formattedDate = article.publishedDate
            ? new Date(article.publishedDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            })
            : '';

        const seoTitle = article.metaTitle || article.title;
        const seoDescription = article.metaDescription || article.excerpt || '';
        const canonicalUrl = `${window.location.origin}/articles/${article.slug}`;

        return (
            <React.Fragment>
                <Helmet>
                    <title>{seoTitle} | Game City</title>
                    <meta name="robots" content={isPreview ? 'noindex, follow' : 'index, follow'} />
                    <meta name="description" content={seoDescription} />
                    <link rel="canonical" href={canonicalUrl} />
                    {article.featuredImageUrl && <meta property="og:image" content={article.featuredImageUrl} />}
                    <meta property="og:title" content={seoTitle} />
                    <meta property="og:description" content={seoDescription} />
                    <meta property="og:url" content={canonicalUrl} />
                    <meta property="og:type" content="article" />
                </Helmet>
                <div className={styles.articleDetail}>
                    <div className={styles.cloudBG} />

                    {isPreview && (
                        <div className={styles.previewBanner}>
                            PREVIEW MODE — This article is not yet published
                        </div>
                    )}

                    {article.featuredImageUrl && (
                        <div className={styles.heroSection}>
                            <img
                                src={cmsImageUrl(article.featuredImageUrl)}
                                alt={article.featuredImageAlt || article.title}
                                className={styles.heroImage}
                            />
                        </div>
                    )}

                    <Container>
                        <div className={styles.articleHeader}>
                            <div className={styles.metaRow}>
                                {article.categoryName && (
                                    <Link
                                        to={`/articles/category/${article.categorySlug}`}
                                        className={styles.categoryBadge}
                                    >
                                        {article.categoryName}
                                    </Link>
                                )}
                                {formattedDate && <span className={styles.date}>{formattedDate}</span>}
                            </div>
                            <h1 className={styles.title}>{article.title}</h1>
                            <p className={styles.authorLine}>By {article.authorName || 'Game City'}</p>
                        </div>

                        <article className={styles.articleBody}>
                            <BlockRenderer content={article.content} />
                        </article>

                        {article.videoUrl && (
                            <div className={styles.videoSection}>
                                <div className={styles.videoWrapper}>
                                    <ReactPlayer url={article.videoUrl} controls width="100%" height="100%" />
                                </div>
                            </div>
                        )}

                        {article.ctaText && (
                            <div className={styles.ctaSection}>
                                <p className={styles.ctaText}>{article.ctaText}</p>
                                {article.ctaButtonText && article.ctaButtonUrl && (
                                    article.ctaButtonOpenInNewTab ? (
                                        <a
                                            href={article.ctaButtonUrl}
                                            className={styles.button}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {article.ctaButtonText}
                                        </a>
                                    ) : article.ctaButtonUrl.startsWith('/') ? (
                                        <Link to={article.ctaButtonUrl} className={styles.button}>
                                            {article.ctaButtonText}
                                        </Link>
                                    ) : (
                                        <a href={article.ctaButtonUrl} className={styles.button}>
                                            {article.ctaButtonText}
                                        </a>
                                    )
                                )}
                            </div>
                        )}

                        {article.relatedArticles && article.relatedArticles.length > 0 && (
                            <section className={styles.relatedSection}>
                                <h2 className={styles.relatedTitle}>Related Articles</h2>
                                <Row>
                                    {article.relatedArticles.map(related => (
                                        <Col xs={12} md={4} key={related.id} className={styles.relatedItem}>
                                            <ArticleCard article={related} />
                                        </Col>
                                    ))}
                                </Row>
                            </section>
                        )}

                        <div className={styles.backSection}>
                            <Link to="/articles" className={`${styles.button} ${styles.small} ${styles.alternate}`}>Back to All Articles</Link>
                        </div>
                    </Container>
                    <div className={styles.cityScape} />
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, null)(withRouter(ArticleDetail));
